import { parse } from 'cookie';

import { isWacRoutingSignalWae } from './is-wac-routing-signal-wae';

/**
 * Which delivery path served the request.
 * - `gateway`: GlobalEdge → bxp-nginx → bxp-gateway → Bifrost → wac-web
 * - `direct`: GlobalEdge → Bifrost → wac-web (Direct-to-UIP)
 */
export type ServePath = 'gateway' | 'direct';

const SERVE_PATHS: ReadonlySet<string> = new Set<ServePath>(['gateway', 'direct']);

export const isServePath = (value: unknown): value is ServePath =>
	typeof value === 'string' && SERVE_PATHS.has(value);

const GATEWAY_REQUEST_COOKIE = 'is_bxp_gateway_request';

type ServePathHeaders = {
	'x-wac-routing'?: unknown;
	cookie?: unknown;
};

const hasGatewayRequestCookie = (cookieHeader: unknown): boolean => {
	if (Array.isArray(cookieHeader)) {
		return cookieHeader.some(hasGatewayRequestCookie);
	}

	return typeof cookieHeader === 'string' && parse(cookieHeader)[GATEWAY_REQUEST_COOKIE] === 'true';
};

/**
 * bxp-gateway is the only hop that sets `x-wac-routing: wae` on the upstream
 * request. When it also has a gateway request id, it appends the
 * `is_bxp_gateway_request=true` cookie, which covers the bot routes it proxies
 * without the header. Direct-to-UIP requests carry neither, so their absence is
 * the Direct-to-UIP signal. The value feeds the `servePath` UFO cohort (GWP-13569).
 *
 * The value is resolved at render time and baked into the HTML. A page served
 * from the edge cache reports the path of the request that filled the cache.
 */
export const resolveServePath = (headers: ServePathHeaders): ServePath => {
	if (isWacRoutingSignalWae(headers['x-wac-routing'])) {
		return 'gateway';
	}

	if (hasGatewayRequestCookie(headers.cookie)) {
		return 'gateway';
	}

	return 'direct';
};
